import { useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import dayjs from "dayjs";

import { changesSlice, changesSubslice } from "redux/features/changes";
import { RESOURCE_TYPE_AFFILIATION_STATUS } from "redux/typeConstants";

export function useAffiliationChangeInformation(id, dateSpec) {
  const changeRecord = changesSubslice(
    useSelector(changesSlice.selector),
    id,
    "affiliation",
    RESOURCE_TYPE_AFFILIATION_STATUS,
    `${id}/${dateSpec}`
  );
  const changeKeys = useMemo(() => {
    return {
      id,
      section: "affiliation",
      resourceType: RESOURCE_TYPE_AFFILIATION_STATUS,
      resourceId: `${id}/${dateSpec}`,
    };
  }, [id, dateSpec]);

  return [changeRecord, changeKeys];
}

export function useAllAffiliationChangeRecords(id, dates) {
  const records = changesSubslice(
    useSelector(changesSlice.selector),
    id,
    "affiliation"
  );
  // If a change to one record altered the through date of another, a change
  // record based on the old through date won't be applicable any more;
  // delete it.
  const dateValid = Object.fromEntries(dates.map((date) => [date, true]));

  const inapplicableDates = Object.keys(records || {}).filter(
    (date) => !dateValid[date]
  );
  const dispatch = useDispatch();
  useEffect(() =>
    inapplicableDates.forEach((date) => {
      dispatch(
        changesSlice.actions.deleteChangeRecord({
          id,
          section: "affiliation",
          resourceType: RESOURCE_TYPE_AFFILIATION_STATUS,
          resourceId: `${id}/${date}`,
        })
      );
    })
  );

  return records;
}

export const AFFILIATION_CHANGEABLE_FIELDS = [
  "institution_id",
  "new_institution_name",
  "department_name",
  "location",
  "institution_location",
];

export function getStatusSummary(data) {
  const statusRecords = data.affiliation_status_summary.map((s) => {
    return { ...s };
  });
  let prevRecord = null;
  statusRecords.forEach((statusRecord) => {
    if (prevRecord) {
      if (prevRecord.isFirst) prevRecord.isSingle = false;
      const fromDayJS = dayjs(statusRecord.from);
      const fromMonth = fromDayJS.format("YYYY-MM") + "-01";
      const canRound =
        (!prevRecord.from || prevRecord.from < fromMonth) &&
        (!prevRecord.last_used_date || prevRecord.last_used_date < fromMonth);
      const [adjustedFromDayJS, format] = canRound
        ? [fromDayJS.startOf("month"), "MMMM YYYY"]
        : [fromDayJS, "MMMM D, YYYY"];

      prevRecord.showThrough = adjustedFromDayJS
        .subtract(1, "day")
        .format(format);
      prevRecord.roundedThrough = canRound;
      statusRecord.showFrom = adjustedFromDayJS.format(format);
      statusRecord.roundedFrom = canRound;
    } else {
      statusRecord.isFirst = true;
      statusRecord.isSingle = true;
    }
    statusRecord.previousRecord = prevRecord;
    prevRecord = statusRecord;
  });
  return statusRecords;
}
